import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { createDocumentWithContent, type DocDocument, type DocType, type ExcelSheet } from '@/lib/documents';

// ─────────────────────────────────────────────────────────────
// Import de fichiers bureautiques depuis l'appareil du marchand.
//
// Formats acceptés :
//   • Word        → .docx, .txt, .md, .rtf (texte extrait)
//   • Excel       → .xlsx, .xls, .csv (toutes les feuilles conservées)
//   • PowerPoint  → .pptx (une diapositive par slide, titre + contenu)
//   • PDF         → .pdf (conservé tel quel, visible dans le lecteur)
//
// Les fichiers .doc / .xls / .ppt (anciens formats binaires) ne peuvent pas
// être lus dans le navigateur : on invite l'utilisateur à réenregistrer le
// fichier au format récent.
// ─────────────────────────────────────────────────────────────

/** Taille maximale acceptée (au-delà, le navigateur devient trop lent). */
export const MAX_IMPORT_BYTES = 15 * 1024 * 1024;

export interface ImportResult {
  document: DocDocument;
}

const WORD_EXT = ['docx', 'txt', 'md', 'rtf'];
const EXCEL_EXT = ['xlsx', 'xls', 'csv'];
const POWERPOINT_EXT = ['pptx'];
const PDF_EXT = ['pdf'];

export const IMPORT_ACCEPT =
  '.docx,.txt,.md,.rtf,.xlsx,.xls,.csv,.pptx,.pdf';

function extensionOf(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : '';
}

function baseNameOf(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return (idx > 0 ? fileName.slice(0, idx) : fileName).trim() || 'Document importé';
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function textToHtml(text: string): string {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  return lines
    .map((line) => (line.trim() ? `<p>${escapeHtml(line)}</p>` : '<p><br></p>'))
    .join('');
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Extrait les paragraphes d'un document Word (.docx). */
async function docxToHtml(buffer: ArrayBuffer): Promise<string> {
  const zip = await JSZip.loadAsync(buffer);
  const entry = zip.file('word/document.xml');
  if (!entry) throw new Error('Document Word illisible.');
  const xml = await entry.async('string');
  const paragraphs = xml.split('</w:p>');
  const html: string[] = [];
  paragraphs.forEach((chunk) => {
    const matches = Array.from(chunk.matchAll(/<w:t[^>]*>([\s\S]*?)<\/w:t>/g));
    if (matches.length === 0) return;
    const text = matches.map((m) => m[1]).join('').trim();
    if (text) html.push(`<p>${escapeHtml(text)}</p>`);
  });
  return html.join('') || '';
}

/** Découpe une ligne CSV en respectant les guillemets. */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',' || char === ';') {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
}

function rowsToSheet(name: string, rows: string[][]): ExcelSheet {
  const cells: Record<string, string> = {};
  let maxCols = 0;
  rows.forEach((row, r) => {
    maxCols = Math.max(maxCols, row.length);
    row.forEach((value, c) => {
      const text = String(value ?? '').trim();
      if (text) cells[`${r},${c}`] = text;
    });
  });
  return {
    id: `import-${Math.random().toString(36).slice(2, 9)}`,
    name: name.slice(0, 31) || 'Feuille 1',
    cells,
    rows: Math.max(rows.length + 5, 20),
    cols: Math.max(maxCols + 2, 8),
  };
}

function csvToSheets(text: string, fileName: string): ExcelSheet[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim().length > 0);
  const rows = lines.map(parseCsvLine);
  return [rowsToSheet(baseNameOf(fileName), rows)];
}

function workbookToSheets(buffer: ArrayBuffer): ExcelSheet[] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheets: ExcelSheet[] = [];
  workbook.SheetNames.forEach((name) => {
    const sheet = workbook.Sheets[name];
    if (!sheet) return;
    const rows = XLSX.utils.sheet_to_json<string[]>(sheet, {
      header: 1,
      raw: false,
      defval: '',
      blankrows: false,
    });
    sheets.push(rowsToSheet(name, rows.map((row) => row.map((cell) => String(cell ?? '')))));
  });
  return sheets.length > 0 ? sheets : [rowsToSheet('Feuille 1', [])];
}

/** Extrait les diapositives d'un PowerPoint (.pptx). */
async function pptxToSlides(buffer: ArrayBuffer): Promise<Array<{ id: string; title: string; body: string }>> {
  const zip = await JSZip.loadAsync(buffer);
  const slideFiles = Object.keys(zip.files)
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/.test(path))
    .sort((a, b) => {
      const na = Number(a.match(/slide(\d+)\.xml$/)?.[1] || 0);
      const nb = Number(b.match(/slide(\d+)\.xml$/)?.[1] || 0);
      return na - nb;
    });

  const slides: Array<{ id: string; title: string; body: string }> = [];
  for (const path of slideFiles) {
    const entry = zip.file(path);
    if (!entry) continue;
    const xml = await entry.async('string');
    const texts = Array.from(xml.matchAll(/<a:t>([\s\S]*?)<\/a:t>/g))
      .map((m) => m[1].replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim())
      .filter(Boolean);
    if (texts.length === 0) continue;
    slides.push({
      id: `slide-${slides.length + 1}-${Math.random().toString(36).slice(2, 7)}`,
      title: texts[0],
      body: texts.slice(1).join('\n'),
    });
  }
  return slides;
}

function detectType(ext: string): DocType | null {
  if (WORD_EXT.includes(ext)) return 'word';
  if (EXCEL_EXT.includes(ext)) return 'excel';
  if (POWERPOINT_EXT.includes(ext)) return 'powerpoint';
  if (PDF_EXT.includes(ext)) return 'pdf';
  return null;
}

/**
 * Importe un fichier choisi sur l'appareil et le transforme en document
 * éditable dans « Documents ».
 */
export async function importDocumentFile(
  userId: number,
  file: File,
  folderId: number | null = null,
): Promise<DocDocument> {
  const ext = extensionOf(file.name);
  const type = detectType(ext);

  if (!type) {
    if (['doc', 'xls', 'ppt'].includes(ext)) {
      throw new Error(
        `Le format .${ext} (ancienne version Office) n'est pas lisible dans le navigateur. Réenregistrez le fichier en .${ext}x puis réessayez.`,
      );
    }
    throw new Error('Format non pris en charge. Utilisez .docx, .xlsx, .pptx, .csv, .txt ou .pdf.');
  }

  if (file.size > MAX_IMPORT_BYTES) {
    throw new Error('Fichier trop volumineux (15 Mo maximum).');
  }

  if (type === 'pdf' && file.size > 8 * 1024 * 1024) {
    throw new Error('Le PDF est trop volumineux (8 Mo maximum pour ce format).');
  }

  const name = baseNameOf(file.name);
  const buffer = await file.arrayBuffer();

  if (type === 'word') {
    const html = ext === 'docx' ? await docxToHtml(buffer) : textToHtml(new TextDecoder().decode(buffer));
    return createDocumentWithContent(userId, name, 'word', { html }, folderId);
  }

  if (type === 'excel') {
    const sheets = ext === 'csv' ? csvToSheets(new TextDecoder().decode(buffer), file.name) : workbookToSheets(buffer);
    return createDocumentWithContent(
      userId,
      name,
      'excel',
      { sheets, activeSheetId: sheets[0]?.id },
      folderId,
    );
  }

  if (type === 'powerpoint') {
    const slides = await pptxToSlides(buffer);
    if (slides.length === 0) throw new Error('Aucune diapositive lisible dans ce fichier.');
    return createDocumentWithContent(userId, name, 'powerpoint', { slides }, folderId);
  }

  // PDF : conservé tel quel (lecture dans le lecteur intégré).
  return createDocumentWithContent(
    userId,
    name,
    'pdf',
    { pdfBase64: arrayBufferToBase64(buffer), fileName: file.name },
    folderId,
  );
}